import { useState, useEffect, useCallback } from "react";
import { get as apiGet, post, put, del } from "../../../services/api";

/**
 * Custom hook to manage bars with CRUD operations
 * No auto-refresh - only manual refresh after CRUD operations
 * @returns {object} - { bars, loading, error, createBar, updateBar, deleteBar, refetch }
 */
const useBars = () => {
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBars = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGet("/bars");

      if (data.success) {
        setBars(data.bars || []);
        setError(null);
      } else {
        setError(data.error || "Failed to fetch bars");
      }
    } catch (err) {
      console.error("Error fetching bars:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new bar
  const createBar = async (barData) => {
    try {
      const data = await post("/bars", barData);

      if (data.success) {
        // Refresh bars list
        await fetchBars();
        return { success: true, bar: data.bar };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error("Error creating bar:", err);
      return { success: false, error: err.message };
    }
  };

  // Update an existing bar
  const updateBar = async (barId, barData) => {
    if (!barId) return { success: false, error: "Invalid bar ID" };

    try {
      const data = await put(`/bars/${barId}`, barData);

      if (data.success) {
        // Refresh bars list
        await fetchBars();
        return { success: true, bar: data.bar };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error("Error updating bar:", err);
      return { success: false, error: err.message };
    }
  };

  // Delete a bar
  const deleteBar = async (barId) => {
    if (!barId) return { success: false, error: "Invalid bar ID" };

    try {
      const data = await del(`/bars/${barId}`);

      if (data.success) {
        // Refresh bars list
        await fetchBars();
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (err) {
      console.error("Error deleting bar:", err);
      return { success: false, error: err.message };
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchBars();
  }, [fetchBars]);

  return {
    bars,
    loading,
    error,
    createBar,
    updateBar,
    deleteBar,
    refetch: fetchBars,
  };
};

export default useBars;
